import { useMemo } from 'react';

/**
 * Lifted from Storefront.jsx
 *
 * Hook for persisting and restoring carousel slide positions in browser history.
 * Aims to improve the UX when navigating back to a page with a carousel from a PDP.
 *
 * NOTE: Only works with history navigation (back/forward buttons) and does not
 * persist across page refreshes or direct URL access.
 *
 * @param {string} carouselName - Unique identifier for the carousel
 * @returns {Object} Object containing functions to get and set carousel history
 * @returns {number|undefined} Object.savedSlideIndex - Previously saved slide index, if available
 * @returns {Function} Object.saveSlideIndex - Function to save current slide index
 */
function useCarouselHistory(carouselName) {
    const savedSlideIndex = useMemo(() => {
        if (typeof window === 'undefined' || !carouselName) {
            return undefined;
        }

        const carouselHistory =
            window.history.state && window.history.state.carouselHistory ? window.history.state.carouselHistory : {};
        return Object.prototype.hasOwnProperty.call(carouselHistory, carouselName)
            ? carouselHistory[carouselName]
            : undefined;
    }, [carouselName]);

    const saveSlideIndex = (slideIndex) => {
        // Validate slideIndex parameter
        if (typeof slideIndex !== 'number' || Number.isNaN(slideIndex) || slideIndex < 0) {
            throw new Error(`Invalid slideIndex: ${slideIndex}. Must be a non-negative number.`);
        }

        if (typeof window === 'undefined' || !carouselName) {
            return;
        }

        window.history.replaceState(
            {
                ...window.history.state,
                carouselHistory: {
                    ...(window.history.state ? window.history.state.carouselHistory : {}),
                    [carouselName]: slideIndex,
                },
            },
            ''
        );
    };

    return { saveSlideIndex, savedSlideIndex };
}

export default useCarouselHistory;
